/** @jsxImportSource @emotion/react */
import Root from "pages/root";
import {
  Container,
  SubHeaderContainer,
  HeaderContainer,
  HeaderTitle,
  ContentContainer,
  EmptyRow,
  FooterContainer,
} from "./index.styles";
import UI from "@mile-meeting/mile-ui";
import ToolTip from "uis/new-tootip";
import ComplainCategory from "./component/complain-category";
import { useEffect, useState } from "react";
import {
  getComplainCategories,
  TComplainCategory,
  updateComplainCategories,
} from "apis/meeting-room-complain/category";
import { useToast } from "hook/useToast";
import { useLocation, useNavigate } from "react-router-dom";

const ComplaintCategories = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [categories, setCategories] = useState<TComplainCategory[]>([]);

  const onHandleAddCategory = () => {
    setCategories([...categories, {}]);
  };

  const onHandleUpdateCategory = (updateIdx: number, value: string) => {
    const updateCategories = categories.map((category, idx) => {
      if (idx === updateIdx) {
        return { ...category, content: value };
      }
      return category;
    });
    setCategories(updateCategories);
  };

  const onHandleDeleteCategory = (deleteIdx: number) => {
    let updateCategories = categories.filter((_, idx) => idx !== deleteIdx);
    setCategories(updateCategories);
  };

  const onHandleSave = async () => {
    const saveCategories = categories
      .filter((category) => category.content?.trim() !== "")
      .map((category) => category.content ?? "");
    const res = await updateComplainCategories(saveCategories);
    if (res.success) {
      navigate("/meeting/회의실 민원 관리/민원 유형 설정", { state: { updateSuccess: true }, replace: true });
    } else {
      showToast(res.message ?? "처리 중 오류가 발생했습니다. 다시 시도해 주세요.", "success");
    }
  };

  const getCategories = async () => {
    const res = await getComplainCategories();
    if (res.success) {
      setCategories(res.data ?? []);
    }
  };

  useEffect(() => {
    getCategories();
  }, []);

  useEffect(() => {
    if (location.state?.updateSuccess) {
      showToast("설정이 업데이트 되었습니다.", "success");
      window.history.replaceState({}, "");

      setCategories([]);
      getCategories();
    }
  }, [location]);

  return (
    <Container>
      <HeaderContainer>
        <UI.Typography
          type="title03"
          fontStyle="bold"
          color="var(--alias-color-label-netural)"
          style={{ padding: "24px 20px" }}
        >
          민원 유형 설정
        </UI.Typography>
        <SubHeaderContainer>
          <HeaderTitle>
            <UI.Typography type="headline01" fontStyle="medium" color="var(--alias-color-label-normal)">
              회의실 민원 유형 목록
            </UI.Typography>
            <ToolTip
              position={{
                top: 24,
                left: 0,
              }}
              icon="info"
              text={
                <p>
                  회의실에서 발생할 수 있는 민원 유형을 등록하시면 태블릿에서 유형을 선택하여 바로 민원 신청이
                  가능합니다.
                </p>
              }
            />
          </HeaderTitle>
          <UI.Button title="저장하기" onClick={onHandleSave} primary={false} />
        </SubHeaderContainer>
      </HeaderContainer>
      <ContentContainer>
        {categories.length === 0 ? (
          <EmptyRow>
            <UI.Icon name="dotHorizontalCircle" color="var(--alias-color-label-alternative)" size={24} />
            <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
              회의실 민원 유형이 없습니다. 간편한 민원 관리를 위해 유형을 추가해 주세요.
            </UI.Typography>
          </EmptyRow>
        ) : (
          categories.map((category, idx) => {
            return (
              <ComplainCategory
                idx={idx}
                category={category}
                onChange={onHandleUpdateCategory}
                onDelete={onHandleDeleteCategory}
              />
            );
          })
        )}
      </ContentContainer>
      <FooterContainer>
        <div style={{ padding: "0px 24px" }}>
          <UI.Button
            type="text"
            size="medium"
            title="추가하기"
            prefixIcon="addCircle"
            showPrefixIcon
            onClick={onHandleAddCategory}
          />
        </div>
      </FooterContainer>
    </Container>
  );
};

export default ComplaintCategories;
